import { DataTable } from '@/components/DataTable/DataTable';
import { useParams } from 'react-router-dom';
import { useState, Fragment, useEffect, useContext } from 'react';
import { useGet } from '@/query/fetchHooks';
import {
  PersonAvatar,
  Tag,
  InputText,
  Tabs,
  FilterList,
  TEOLoader,
} from '@teo/components';
import { Search } from '@teo/components/icons';
import { T } from '@/components/T';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Userwithorganisationwithidps } from '@/models';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { newUid, useMobile, downloadLink } from '@/util';
import MobileSubPageMenu from '@/components/HeaderMobile/MobileSubPageMenu';
import { NavContext } from '@/components/NavContext';
import OptionsAction from '@/components/DataTable/OptionsAction';
import { axios } from '@/axios';
import { config } from '@/config';
import { NotificationContext } from '@/components/Contexts/NotificationContext';

function FollowUpCompetenciesPage() {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const { organisationId } = useParams();
  const { setSubPage } = useContext(NavContext);
  const queryParams = new URLSearchParams(window.location.search);
  const activeTab = queryParams.get('tab');
  const { setShowNotification } = useContext(NotificationContext);

  const { auth } = useContext(AuthContext);
  const authUser = auth?.user as Userwithorganisationwithidps;

  const [tab, setTab] = useState(activeTab ? activeTab : 'teams');
  const [filterTeams, setFilterTeams] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterProfiles, setFilterProfiles] = useState('');
  const [loadTab, setLoadTab] = useState(true);

  const [refetchTable, setRefetchTable] = useState<string>(newUid(20));

  const [selectedStatusTeams, setSelectedStatusTeams] = useState([
    {
      label: t('pages.follow_up_courses.status_active'),
      value: 'active',
    },
  ]);

  const [debouncedFilterTeams, setDebouncedFilterTeams] = useState('');

  useEffect(() => {
    if (!activeTab) return;
    const params = new URLSearchParams(window.location.search);
    params.delete('tab');
    const newUrl =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : '') +
      window.location.hash;
    window.history.replaceState({}, '', newUrl);
  }, []);

  useEffect(() => {
    setSubPage(true);
    return () => setSubPage(false);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilterTeams(filterTeams), 250);
    return () => clearTimeout(t);
  }, [filterTeams]);

  const [debouncedFilterUser, setDebouncedFilterUser] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilterUser(filterUser), 250);
    return () => clearTimeout(t);
  }, [filterUser]);

  const [debouncedFilterProfiles, setDebouncedFilterProfiles] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilterProfiles(filterProfiles), 250);
    return () => clearTimeout(t);
  }, [filterProfiles]);

  const [filterStatusTeams, setFilterStatusTeams] = useState<any>();

  const basePath = window.location.pathname;

  useEffect(() => {
    if (selectedStatusTeams.length == 0) {
      setFilterStatusTeams(null);
    }
    if (selectedStatusTeams.length == 1) {
      setFilterStatusTeams(`${selectedStatusTeams[0].value}`);
    }
    if (selectedStatusTeams.length > 1) {
      const select = selectedStatusTeams?.map((el) => el.value);
      setFilterStatusTeams(`${select}`);
    }
    setRefetchTable(newUid(20));
  }, [selectedStatusTeams]);

  const formatPercent = (value: any) => {
    const number = Number(value || 0);
    return `${Math.round(number * 100)}%`;
  };

  const teamColumns: any = [
    {
      title: t('columns.teamName'),
      id: 'teamName',
      className: 'col-start-1 col-end-8 row-start-1',
      render: (team: any) => (
        <div
          style={{
            marginLeft: '' + (team?.teamPath?.split(';;')?.length - 1) + 'rem',
          }}
          className="flex items-center"
        >
          <PersonAvatar
            inline
            id={team.id}
            name={team.teamName}
            bgColor={team.color ? team.color : null}
          ></PersonAvatar>
          <p className="text-[12px] font-medium text-[#231f20] sm:text-sm">
            {team.teamName}
          </p>
        </div>
      ),
    },
    {
      addTitleOnMobile: true,
      className: 'col-start-1 col-end-8 row-start-2',
      id: 'nrOfTeamMembers',
      title: t('columns.nrOfTeamMembers'),
      align: 'right',
      classNameDesktop: 'max-w-[175px] w-[175px]',
    },
    {
      sortable: false,
      id: 'space',
      classNameDesktop: 'max-w-[25px] w-[25px]',
    },
  ];

  const userColumns: any = [
    {
      title: t('columns.username'),
      id: 'username',
      className: 'col-start-1 col-end-9 break-all',
      render: (usr: any) => (
        <div className="flex w-full items-center">
          <PersonAvatar
            inline
            id={usr.id}
            name={usr.username}
            imageSrc={usr.picture}
          ></PersonAvatar>
          <div className="flex flex-col">
            <span className="font-medium">{usr.username}</span>
            <span className="text-xs font-normal text-[#7D7D81]">
              {usr.email}
            </span>
          </div>
        </div>
      ),
    },
    {
      addTitleOnMobile: true,
      className: 'col-start-6 col-end-10 row-start-2 justify-end',
      id: 'created',
      title: t('columns.status'),
      align: 'right',
      classNameDesktop: 'max-w-[125px] w-[125px]',
      render: (usr: any) => (
        <Tag
          title={
            usr.disabled
              ? t('pages.manage_users.disabled')
              : usr.created
              ? t('pages.manage_users.active')
              : t('pages.manage_users.not_active')
          }
          variant={usr.created && !usr.disabled ? 'success' : 'error'}
        />
      ),
    },
    {
      addTitleOnMobile: true,
      className: 'col-start-1 col-end-6 row-start-2',
      id: 'lastActive',
      title: t('columns.lastActive'),
      align: 'right',
      classNameDesktop: 'max-w-[175px] w-[175px]',
      render: (usr: any) => format(new Date(usr.lastActive), 'dd/MM/yyyy'),
    },
    {
      title: '',
      id: 'edit',
      sortable: false,
      className: 'col-start-9 row-start-1 justify-end',
      classNameDesktop: 'max-w-[65px] w-[65px]',
      render: (usr: any) => (
        <OptionsAction
          menuItems={[
            {
              id: usr.id + 'report',
              addOnMobile: true,
              title: t('report.courseOverviewCompetences'),
              onClick: async () => {
                try {
                  const result: any = await axios.post(
                    `${config.teoreport}/report`,
                    {
                      download: true,
                      lang: authUser?.lang,
                      organisationId: organisationId,
                      formType: 'competenceOverview',
                      userIds: [usr.id],
                    }
                  );

                  if (result?.data?.link) {
                    downloadLink(result.data.link);
                  }
                  setShowNotification({
                    message: t('pages.report_page.report_download_message'),
                    delay: 5000,
                  });
                } catch (error: any) {
                  console.error('Report request failed:', error);

                  const errorMessage =
                    error?.response?.data?.message ||
                    error?.response?.data?.error ||
                    t('pages.report.report_error');

                  setShowNotification({
                    message: errorMessage,
                    delay: 5000,
                  });
                }
              },
            },
          ]}
          // screensUpdateTable="mobile"
          rowInfo={usr}
          mobileHeader={{
            render: (usr: any) => (
              <div className="flex items-center">
                <PersonAvatar
                  inline
                  id={usr.id}
                  name={usr.username}
                  imageSrc={usr.picture}
                ></PersonAvatar>

                <div>
                  <p className="text-[12px] font-medium text-[#231f20] sm:text-sm">
                    {usr.username}
                  </p>
                </div>
              </div>
            ),
          }}
        />
      ),
    },
    // {
    //   sortable: false,
    //   id: 'space',
    //   classNameDesktop: 'max-w-[25px] w-[25px]',
    // },
  ];

  const profileColumns: any = [
    {
      title: t('columns.profile'),
      id: 'professionName',
      className: 'col-start-1 col-end-10 row-start-1',
      render: (profile: any) => (
        <div className="flex flex-col">
          <span className="text-[12px] font-medium text-[#231f20] sm:text-sm">
            {profile.professionName}
          </span>
          <span className="text-xs font-normal text-[#7D7D81]">
            {t('pages.follow_up_users.profile_members', {
              count: profile.nrOfMembers || 0,
            })}
          </span>
        </div>
      ),
    },
    {
      addTitleOnMobile: true,
      className: 'col-start-10 col-end-12 row-start-1 justify-end',
      id: 'nrOfQualified',
      title: t('pages.follow_up_users.qualified'),
      align: 'right',
      render: (profile: any) => (
        <span className="text-[12px] font-medium text-[#231f20] sm:text-sm">
          {profile.nrOfQualified || 0}/{profile.nrOfMembers || 0}
        </span>
      ),
    },
    {
      addTitleOnMobile: true,
      className: 'col-start-1 col-end-12 row-start-2',
      id: 'progress',
      title: t('pages.follow_up_users.average_coverage'),
      align: 'right',
      render: (profile: any) => {
        const progress = Math.max(
          0,
          Math.min(1, Number(profile.progress || 0))
        );

        return (
          <div className="flex min-w-[180px] items-center gap-2">
            <div className="h-2 flex-1 rounded-full bg-[#E9E9EB]">
              <div
                className={`h-2 rounded-full ${
                  progress >= 0.8 ? 'bg-[#24A148]' : 'bg-[#F1C21B]'
                }`}
                style={{ width: `${Math.round(progress * 100)}%` }}
              />
            </div>
            <span className="min-w-[38px] text-right text-xs font-semibold tabular-nums">
              {formatPercent(progress)}
            </span>
          </div>
        );
      },
    },
  ];

  const FILTERS_STATUS = [
    {
      label: t('pages.follow_up_courses.status_active'),
      value: 'active',
    },
    {
      label: t('pages.follow_up_courses.status_inactive'),
      value: 'disabled,hidden',
    },
  ];

  return (
    <div className="max-w-6xl pb-8">
      {isMobile ? (
        <MobileSubPageMenu title={'links.competencies'} />
      ) : (
        <h1 className="mt-8 mb-4 text-xl font-semibold text-black">
          <T>{t('links.competencies')}</T>
        </h1>
      )}
      <div className="font-medium">
        <Tabs
          activeName={tab}
          onChange={(tab: any) => {
            setTab(tab);
            setLoadTab(true);
            setFilterTeams('');
            setFilterUser('');
            setFilterProfiles('');
          }}
        >
          <Tabs.TabButton name="teams">
            {t('pages.manage_users.team_tab')}
          </Tabs.TabButton>
          <Tabs.TabButton name="users">
            {t('pages.manage_users.users_tab')}
          </Tabs.TabButton>
          <Tabs.TabButton name="profiles">
            {t('pages.follow_up_users.profiles_tab')}
          </Tabs.TabButton>
        </Tabs>
      </div>
      {loadTab && (
        <div className="mx-auto flex min-h-[60vh] w-full justify-center py-20 px-10">
          <TEOLoader loaderSrc="/images/TEOloader.gif" className="w-[110px]" />
        </div>
      )}

      {tab === 'teams' && (
        <Fragment>
          <div className={`${loadTab && 'hidden'}`}>
            <div className="py-8 md:w-1/2">
              <InputText
                value={filterTeams}
                onChange={(ev: any) => setFilterTeams(ev.target.value)}
                Icon={Search}
                placeholder={t('search.team')}
                aria-label={t('search.team')}
              />
            </div>
            <div className="mb-5 flex items-center">
              <FilterList
                filters={FILTERS_STATUS}
                onSelect={setSelectedStatusTeams}
                selected={selectedStatusTeams}
              />
            </div>
            <DataTable
              filter={debouncedFilterTeams}
              key={refetchTable}
              rowPath={(team) => `${basePath}/team/${team.id}`}
              defaultSort={[{ id: 'teamPath', desc: false }]}
              fetch={useGet.bind(
                null,
                (authUser?.role || 0) >= 90 ? '/teams' : '/myteams',
                {
                  userId:
                    (authUser?.role || 0) >= 90 ? undefined : authUser?.id,
                  organisationId,
                  teamStatus: filterStatusTeams,
                  onSuccess: (result: any) => {
                    setLoadTab(false);
                  },
                }
              )}
              empty={{
                template: 1,
                text: t('empty_tenplate.team_list'),
              }}
              screensUpdateTable="mobile"
              indent={(team: any) => {
                return team?.teamPath?.split(';;')?.length - 1;
              }}
              columns={teamColumns}
            />
          </div>
        </Fragment>
      )}

      {tab === 'users' && (
        <Fragment>
          <div className={`${loadTab && 'hidden'}`}>
            <div className="py-8 md:w-1/2">
              <InputText
                value={filterUser}
                onChange={(ev: any) => setFilterUser(ev.target.value)}
                Icon={Search}
                placeholder={t('search.user')}
                aria-label={t('search.user')}
              />
            </div>
            <DataTable
              filter={debouncedFilterUser}
              rowPath={(usr) => `${basePath}/user/${usr.id}`}
              defaultSort={[{ id: 'username', desc: false }]}
              fetch={useGet.bind(
                null,
                (authUser?.role || 0) >= 90 ? '/memberdetails' : '/mymembers',
                {
                  userId:
                    (authUser?.role || 0) >= 90 ? undefined : authUser?.id,
                  organisationId,
                  onSuccess: (result: any) => {
                    setLoadTab(false);
                  },
                }
              )}
              empty={{
                template: 1,
                text: t('empty_tenplate.user_list'),
              }}
              columns={userColumns}
            />
          </div>
        </Fragment>
      )}

      {tab === 'profiles' && (
        <Fragment>
          <div className={`${loadTab && 'hidden'}`}>
            <div className="py-8 md:w-1/2">
              <InputText
                value={filterProfiles}
                onChange={(ev: any) => setFilterProfiles(ev.target.value)}
                Icon={Search}
                placeholder={t('pages.follow_up_users.search_profile')}
              />
            </div>
            <DataTable
              filter={debouncedFilterProfiles}
              rowPath={(profile) =>
                `${basePath}/profile/${profile.professionId}`
              }
              defaultSort={[{ id: 'professionName', desc: false }]}
              fetch={useGet.bind(null, '/organisationprofessiondetails', {
                organisationId,
                onSuccess: (result: any) => {
                  setLoadTab(false);
                },
              })}
              empty={{
                template: 1,
                text: t('empty_tenplate.profile_list'),
              }}
              columns={profileColumns}
            />
          </div>
        </Fragment>
      )}
    </div>
  );
}

export default FollowUpCompetenciesPage;
